import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import { Separator } from "@shared/components/ui/separator";
import {
  ArrowLeft,
  Package,
  User,
  Phone,
  MapPin,
  Calendar,
  Truck,
  FileText,
  Edit,
  Download,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { toast } from "sonner";

import { getPickupWithTimeline } from "@features/pickups/services/pickupService";
import EnhancedPickupStatusManager from "@/features/packages/components/EnhancedPickupStatusManager";
import PickupTrackingTimeline from "../components/PickupTrackingTimeline";
import { useAuth } from "@features/auth/contexts/AuthContext";
import HeadLayout from "@shared/layouts/HeadLayout";

export function ViewPickupPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isCollector } = useAuth();

  // Fetch pickup data with timeline
  const {
    data: pickup,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["pickup", id],
    queryFn: () => getPickupWithTimeline(id),
    enabled: !!id,
  });

  const formatDateTime = (date) => {
    return format(new Date(date), "MMM dd, yyyy HH:mm");
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case "signed":
        return "default";
      case "return_collection":
        return "destructive";
      case "order_received":
      case "order_confirmed":
        return "secondary";
      default:
        return "outline";
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("common.back")}
          </Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Loading skeletons */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-4/5"></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("common.back")}
          </Button>
        </div>
        <Card>
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">
              {t("pickupTracking.errorLoadingPickup")}
            </h2>
            <p className="text-muted-foreground mb-4">
              {error.message || t("pickupTracking.failedToLoadPickup")}
            </p>
            <Button onClick={() => refetch()}>{t("common.tryAgain")}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!pickup) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("Back")}
          </Button>
        </div>
        <Card>
          <CardContent className="p-6 text-center">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">
              {t("pickupTracking.pickupNotFound")}
            </h2>
            <p className="text-muted-foreground">
              {t("pickupTracking.noPermissionToView")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const canUpdateStatus =
    user?.role === "admin" ||
    user?.role === "manager" ||
    (isCollector && pickup.collector?.id === user?._id);
  return (
    <div className="mx-auto py-6 space-y-6">
      <HeadLayout title={t("navigation.pickupsView")}>
        {canUpdateStatus && (
          <EnhancedPickupStatusManager
            pickupData={pickup}
            onUpdate={refetch}
            trigger={
              <Button variant="outline" size="sm">
                <Edit className="h-4 w-4 mr-2" />
                {t("pickup.updateStatus")}
              </Button>
            }
          />
        )}
      </HeadLayout>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Timeline Section */}
        <div className="lg:col-span-2 space-y-6">
          <PickupTrackingTimeline pickup={pickup} />
        </div>

        {/* Sidebar Information */}
        <div className="space-y-6">
          {/* Pickup Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                {t("pickup.pickupDetails")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="font-medium">{pickup.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {pickup.user?.brandName || pickup.user?.name}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{pickup.phone}</span>
                </div>

                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <div>{pickup.address}</div>
                    <div className="text-sm text-muted-foreground">
                      {pickup.pickupCity}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>{formatDateTime(pickup.createdAt)}</span>
                </div>

                <Badge
                  variant={getStatusBadgeVariant(pickup.status)}
                  className="text-sm"
                >
                  {pickup.status === "signed" && (
                    <CheckCircle className="h-3 w-3 mr-1" />
                  )}
                  {pickup.status === "return_collection" && (
                    <AlertCircle className="h-3 w-3 mr-1" />
                  )}
                  {t(pickup.status)}
                </Badge>
              </div>

              <Separator />

              <div className="space-y-3">
                <div>
                  <span className="font-medium">{t("products.type")}: </span>
                  <Badge variant="outline">{t(pickup.type)}</Badge>
                </div>

                <div>
                  <span className="font-medium">
                    {t("packages.quantity")}:{" "}
                  </span>
                  {pickup.type === "product" &&
                  pickup.products &&
                  pickup.products.length > 1 ? (
                    <div className="mt-1">
                      <div className="text-sm">
                        {pickup.products.reduce(
                          (total, item) => total + item.quantity,
                          0
                        )}{" "}
                        {t("main.total items")}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {pickup.products.length} {t("main.different products")}
                      </div>
                    </div>
                  ) : pickup.type === "package" &&
                    pickup.packages &&
                    pickup.packages.length > 1 ? (
                    <div className="mt-1">
                      <div className="text-sm">
                        {pickup.packages.length} {t("main.packages")}
                      </div>
                    </div>
                  ) : (
                    <span>{pickup.quantity}</span>
                  )}
                </div>

                {pickup.type === "product" &&
                  pickup.products &&
                  pickup.products.some((item) => item.approvedQuantity) && (
                    <div>
                      <span className="font-medium">
                        {t("Approved Quantity")}:{" "}
                      </span>
                      {pickup.products.length > 1 ? (
                        <div className="mt-1 text-sm">
                          {pickup.products
                            .filter((item) => item.approvedQuantity)
                            .reduce(
                              (total, item) => total + item.approvedQuantity,
                              0
                            )}{" "}
                          {t("main.total approved")}
                        </div>
                      ) : (
                        <span>{pickup.approvedQuantity}</span>
                      )}
                    </div>
                  )}
              </div>

              {pickup.note && (
                <>
                  <Separator />
                  <div>
                    <div className="font-medium mb-2">
                      {t("pickupTracking.notes")}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {pickup.note}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Livreur Information */}
          {pickup.collector && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5" />
                  {t("pickup.assignedLivreur")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">
                    {pickup.collector.fullName}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{pickup.collector.phone}</span>
                </div>

                {pickup.assignedAt && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">
                      {t("pickup.assigned")} {formatDateTime(pickup.assignedAt)}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Package Information */}
          {pickup.type === "package" &&
            pickup.packages &&
            pickup.packages.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    {t("navigation.packages")} ({pickup.packages.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pickup.packages.slice(0, 3).map((pkg, index) => (
                      <div
                        key={pkg._id || index}
                        className="border rounded-lg p-3 bg-muted/50"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium text-sm">
                            #{pkg._id || `Package ${index + 1}`}
                          </div>
                          {pkg.status && (
                            <Badge variant="outline" className="text-xs">
                              {t(pkg.status)}
                            </Badge>
                          )}
                        </div>

                        {pkg.customer && (
                          <div className="space-y-1 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <User className="h-3 w-3" />
                              <span>{pkg.customer.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone className="h-3 w-3" />
                              <span>{pkg.customer.phone}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="h-3 w-3" />
                              <span>{pkg.customer.city}</span>
                            </div>
                          </div>
                        )}

                        {pkg.totalAmount && (
                          <div className="mt-2 text-sm font-medium">
                            {t("main.Amount")}: {pkg.totalAmount} {t("main.DH")}
                          </div>
                        )}

                        {/* Products in package */}
                        {pkg.products && pkg.products.length > 0 && (
                          <div className="mt-2">
                            <div className="text-xs text-muted-foreground mb-1">
                              {t("products.products")} ({pkg.products.length}):
                            </div>
                            <div className="space-y-1">
                              {pkg.products
                                .slice(0, 2)
                                .map((product, prodIndex) => (
                                  <div
                                    key={prodIndex}
                                    className="text-xs bg-background rounded px-2 py-1"
                                  >
                                    {product.name} × {product.quantity}
                                    {product.price && (
                                      <span className="text-muted-foreground ml-1">
                                        ({product.price} {t("main.DH")})
                                      </span>
                                    )}
                                  </div>
                                ))}
                              {pkg.products.length > 2 && (
                                <div className="text-xs text-muted-foreground">
                                  +{pkg.products.length - 2}{" "}
                                  {t("main.more products")}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}

                    {pickup.packages.length > 3 && (
                      <div className="text-sm text-muted-foreground text-center py-2 border-t">
                        {t("main.And")} {pickup.packages.length - 3}{" "}
                        {t("main.more packages")}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

          {/* Product Information - Support both single product (legacy) and multi-product */}
          {pickup.type === "product" && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  {pickup.products && pickup.products.length > 1
                    ? `${t("products.products")} (${pickup.products.length})`
                    : t("products.product")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Multi-product support */}
                  {pickup.products && pickup.products.length > 0
                    ? pickup.products.map((productItem, index) => (
                        <div
                          key={index}
                          className="border rounded-lg p-3 bg-muted/50"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-medium">
                              {productItem.product?.name ||
                                `Product ${index + 1}`}
                            </div>
                            <div className="text-sm font-medium">
                              × {productItem.quantity}
                            </div>
                          </div>

                          <div className="space-y-1 text-sm text-muted-foreground">
                            {productItem.product?.price && (
                              <div>
                                {t("products.price")}:{" "}
                                {productItem.product.price} {t("main.DH")}{" "}
                                {t("main.each")}
                              </div>
                            )}

                            <div>
                              {t("main.Requested Quantity")}:{" "}
                              {productItem.quantity}
                            </div>

                            {productItem.approvedQuantity && (
                              <div className="text-green-600 font-medium">
                                {t("main.Approved Quantity")}:{" "}
                                {productItem.approvedQuantity}
                              </div>
                            )}

                            {productItem.product?.price && (
                              <div className="font-medium text-foreground pt-1 border-t">
                                {t("main.Subtotal")}:{" "}
                                {(
                                  productItem.product.price *
                                  productItem.quantity
                                ).toFixed(2)}{" "}
                                {t("main.DH")}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    : /* Legacy single product support */
                      pickup.product && (
                        <div className="border rounded-lg p-3 bg-muted/50">
                          <div className="font-medium mb-2">
                            {pickup.product.name}
                          </div>
                          <div className="space-y-1 text-sm text-muted-foreground">
                            {pickup.product.price && (
                              <div>
                                {t("products.price")}: {pickup.product.price}{" "}
                                {t("main.DH")} {t("main.each")}
                              </div>
                            )}
                            <div>
                              {t("main.Requested Quantity")}: {pickup.quantity}
                            </div>
                            {pickup.approvedQuantity && (
                              <div className="text-green-600 font-medium">
                                {t("main.Approved Quantity")}:{" "}
                                {pickup.approvedQuantity}
                              </div>
                            )}
                            {pickup.product.price && (
                              <div className="font-medium text-foreground pt-1 border-t">
                                {t("main.Total")}:{" "}
                                {(
                                  pickup.product.price * pickup.quantity
                                ).toFixed(2)}{" "}
                                {t("main.DH")}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                  {/* Total summary for multi-product pickups */}
                  {pickup.products && pickup.products.length > 1 && (
                    <div className="border-t pt-3 mt-3">
                      <div className="flex justify-between items-center font-medium">
                        <span>{t("main.Total Products")}:</span>
                        <span>{pickup.products.length}</span>
                      </div>
                      <div className="flex justify-between items-center font-medium">
                        <span>{t("main.Total Quantity")}:</span>
                        <span>
                          {pickup.products.reduce(
                            (total, item) => total + item.quantity,
                            0
                          )}
                        </span>
                      </div>
                      {pickup.products.every((item) => item.product?.price) && (
                        <div className="flex justify-between items-center font-medium text-lg border-t pt-2 mt-2">
                          <span>{t("main.Total Amount")}:</span>
                          <span>
                            {pickup.products
                              .reduce(
                                (total, item) =>
                                  total + item.product.price * item.quantity,
                                0
                              )
                              .toFixed(2)}{" "}
                            {t("main.DH")}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

export default ViewPickupPage;
